import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import hash from 'object-hash';

/**
 * useSequenceManager Hook
 * 
 * Manages sequence/step state, inheritance, and switching logic.
 * Handles all step-related operations including:
 * - Step switching with data persistence
 * - Step inheritance from previous steps
 * - Step data loading and saving
 * - History management per step
 */
export const useSequenceManager = ({
  // Canvas state
  playersOnCanvas,
  utilitiesOnCanvas,
  textBoxes,
  drawnLines,
  imagesOnCanvas,
  visionCones,
  
  // Canvas state setters
  setPlayersOnCanvas,
  setUtilitiesOnCanvas,
  setTextBoxes,
  setDrawnLines,
  setImagesOnCanvas,
  setVisionCones,
  
  // Other dependencies
  isLoadedFromFirestore,
  canvasLoadedRef,
  draggingPlayerId,
  draggingUtilityId,
  draggingTextId,
  draggingImageId,
}) => {
  // ============================================
  // STATE
  // ============================================
  const [activeStep, setActiveStep] = useState(1);
  const [justLoaded, setJustLoaded] = useState(false);
  const [stepSwitching, setStepSwitching] = useState(false);
  const [loadedStepsLocked, setLoadedStepsLocked] = useState(false);
  
  const [scenarioData, setScenarioData] = useState(() => {
    const initial = {};
    for (let i = 1; i <= 10; i++) {
      initial[i] = {
        stepId: i,
        playersOnCanvas: [],
        utilitiesOnCanvas: [],
        textBoxes: [],
        drawnLines: [],
        imagesOnCanvas: [],
        visionCones: [],
      };
    }
    return initial;
  });

  // ============================================
  // REFS
  // ============================================
  const historyRef = useRef(
    Array.from({ length: 10 }).reduce((acc, _, i) => {
      acc[i + 1] = { past: [], future: [] };
      return acc;
    }, {})
  );
  const suspendHistoryRef = useRef(false);

  // ============================================
  // EFFECTS
  // ============================================

  // Track if we're currently inheriting to prevent double updates
  const isInheritingRef = useRef(false);
  // Track if watchdog should skip (to prevent loops)
  const skipWatchdogRef = useRef(false);
  // Track the last step that was loaded to prevent re-loading
  const lastLoadedStepRef = useRef(null);

  // Inheritance loader - MUST run BEFORE canvas loading to prevent flicker
  // Use useLayoutEffect to run synchronously before paint, ensuring it completes before canvas loading
  useLayoutEffect(() => {
    if (justLoaded || isLoadedFromFirestore) return;
    
    // Skip if we're already inheriting to prevent loops
    if (isInheritingRef.current) return;

    if (activeStep <= 1) return; // No inheritance for step 1

    const current = scenarioData[activeStep];
    const previous = scenarioData[activeStep - 1];
    if (!previous) return;

    // Check if inheritance should happen
    const latestSnapshot = {
      playersOnCanvas: previous.playersOnCanvas || [],
      utilitiesOnCanvas: previous.utilitiesOnCanvas || [],
      drawnLines: previous.drawnLines || [],
      textBoxes: previous.textBoxes || [],
      imagesOnCanvas: previous.imagesOnCanvas || [],
      visionCones: previous.visionCones || [],
    };
    const latestHash = hash(latestSnapshot);

    if (current?.manuallyModified) return;
    if (current?.sourceHash === latestHash) return;

    const isEmpty = !current || [
      current.playersOnCanvas,
      current.utilitiesOnCanvas,
      current.drawnLines,
      current.textBoxes,
      current.imagesOnCanvas,
      current.visionCones,
    ].every(arr => Array.isArray(arr) && arr.length === 0);

    if (!isEmpty && !current?.wasInherited) return;

    // Mark that we're inheriting BEFORE updating state
    skipWatchdogRef.current = true;
    isInheritingRef.current = true;

    setScenarioData(prev => {
      const inherited = {
        stepId: activeStep,
        wasInherited: true,
        lastInheritedFrom: activeStep - 1,
        sourceHash: latestHash,
        playersOnCanvas: [
          ...JSON.parse(JSON.stringify(latestSnapshot.playersOnCanvas || [])),
          ...JSON.parse(JSON.stringify(current?.playersOnCanvas || []))
        ],
        utilitiesOnCanvas: [
          ...JSON.parse(JSON.stringify(latestSnapshot.utilitiesOnCanvas || [])),
          ...JSON.parse(JSON.stringify(current?.utilitiesOnCanvas || []))
        ],
        drawnLines: [
          ...JSON.parse(JSON.stringify(latestSnapshot.drawnLines || [])),
          ...JSON.parse(JSON.stringify(current?.drawnLines || []))
        ],
        textBoxes: [
          ...JSON.parse(JSON.stringify(latestSnapshot.textBoxes || [])),
          ...JSON.parse(JSON.stringify(current?.textBoxes || []))
        ],
        imagesOnCanvas: [
          ...JSON.parse(JSON.stringify(latestSnapshot.imagesOnCanvas || [])),
          ...JSON.parse(JSON.stringify(current?.imagesOnCanvas || []))
        ],
        visionCones: [
          ...JSON.parse(JSON.stringify(latestSnapshot.visionCones || [])),
          ...JSON.parse(JSON.stringify(current?.visionCones || []))
        ],
      };

      // Update canvas state directly (matching original behavior)
      setPlayersOnCanvas(inherited.playersOnCanvas);
      setUtilitiesOnCanvas(inherited.utilitiesOnCanvas);
      setDrawnLines(inherited.drawnLines);
      setTextBoxes(inherited.textBoxes);
      setImagesOnCanvas(inherited.imagesOnCanvas);
      setVisionCones(inherited.visionCones);
      
      canvasLoadedRef.current = true;
      lastLoadedStepRef.current = activeStep;
      setJustLoaded(true);

      return {
        ...prev,
        [activeStep]: inherited,
      };
    });
    
    // Don't reset flags here - let them stay true so canvas loading effect sees it
    // They'll be reset when justLoaded changes or on next step change
  }, [activeStep, justLoaded, isLoadedFromFirestore]);

  // Reset inheritance flag when justLoaded changes (after inheritance completes)
  useEffect(() => {
    if (justLoaded) {
      // Reset flags after inheritance has completed and canvas is updated
      const timer = setTimeout(() => {
        isInheritingRef.current = false;
        skipWatchdogRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [justLoaded]);

  // Update canvas when step changes (only on activeStep change, not scenarioData change)
  useEffect(() => {
    // Skip if we're currently inheriting - inheritance handles its own updates
    if (isInheritingRef.current) {
      return;
    }

    if (!scenarioData) return;
    
    // Initialize step if it doesn't exist
    if (!scenarioData[activeStep]) {
      setScenarioData(prev => ({
        ...prev,
        [activeStep]: {
          stepId: activeStep,
          playersOnCanvas: [],
          utilitiesOnCanvas: [],
          textBoxes: [],
          drawnLines: [],
          imagesOnCanvas: [],
          visionCones: [],
        }
      }));
      return;
    }

    // Check if inheritance will happen - if so, skip loading and let inheritance handle it
    // This prevents the flicker by avoiding a double state update
    // Must match the exact logic from the inheritance effect
    if (activeStep > 1 && !isLoadedFromFirestore) {
      const current = scenarioData[activeStep];
      const previous = scenarioData[activeStep - 1];
      if (previous) {
        const latestSnapshot = {
          playersOnCanvas: previous.playersOnCanvas || [],
          utilitiesOnCanvas: previous.utilitiesOnCanvas || [],
          drawnLines: previous.drawnLines || [],
          textBoxes: previous.textBoxes || [],
          imagesOnCanvas: previous.imagesOnCanvas || [],
          visionCones: previous.visionCones || [],
        };
        const latestHash = hash(latestSnapshot);
        
        // Match inheritance effect logic exactly
        if (current?.manuallyModified) {
          // Won't inherit, so load normally
        } else if (current?.sourceHash === latestHash) {
          // Won't inherit (hash matches), so load normally
        } else {
          const isEmpty = !current || [
            current.playersOnCanvas,
            current.utilitiesOnCanvas,
            current.drawnLines,
            current.textBoxes,
            current.imagesOnCanvas,
            current.visionCones,
          ].every(arr => Array.isArray(arr) && arr.length === 0);
          
          const shouldInherit = isEmpty || current?.wasInherited;
          if (shouldInherit) {
            // Inheritance will handle the update, so skip loading here
            return;
          }
        }
      }
    }

    const step = scenarioData[activeStep];
    
    // Skip if we already loaded this step and canvas matches
    if (lastLoadedStepRef.current === activeStep) {
      const isAlreadyMatching = 
        JSON.stringify(step.playersOnCanvas || []) === JSON.stringify(playersOnCanvas) &&
        JSON.stringify(step.utilitiesOnCanvas || []) === JSON.stringify(utilitiesOnCanvas) &&
        JSON.stringify(step.textBoxes || []) === JSON.stringify(textBoxes) &&
        JSON.stringify(step.drawnLines || []) === JSON.stringify(drawnLines) &&
        JSON.stringify(step.imagesOnCanvas || []) === JSON.stringify(imagesOnCanvas) &&
        JSON.stringify(step.visionCones || []) === JSON.stringify(visionCones);
      
      if (isAlreadyMatching) {
        canvasLoadedRef.current = true;
        return;
      }
    }

    // Check if canvas state already matches scenarioData to avoid unnecessary updates
    const isAlreadyMatching = 
      JSON.stringify(step.playersOnCanvas || []) === JSON.stringify(playersOnCanvas) &&
      JSON.stringify(step.utilitiesOnCanvas || []) === JSON.stringify(utilitiesOnCanvas) &&
      JSON.stringify(step.textBoxes || []) === JSON.stringify(textBoxes) &&
      JSON.stringify(step.drawnLines || []) === JSON.stringify(drawnLines) &&
      JSON.stringify(step.imagesOnCanvas || []) === JSON.stringify(imagesOnCanvas) &&
      JSON.stringify(step.visionCones || []) === JSON.stringify(visionCones);
    
    if (isAlreadyMatching) {
      lastLoadedStepRef.current = activeStep;
      canvasLoadedRef.current = true;
      return;
    }

    canvasLoadedRef.current = false;

    setPlayersOnCanvas(step.playersOnCanvas || []);
    setUtilitiesOnCanvas(step.utilitiesOnCanvas || []);
    setDrawnLines(step.drawnLines || []);
    setTextBoxes(step.textBoxes || []);
    setImagesOnCanvas(step.imagesOnCanvas || []);
    setVisionCones(step.visionCones || []);

    lastLoadedStepRef.current = activeStep;
    canvasLoadedRef.current = true;
  }, [activeStep]); // Only depend on activeStep, NOT justLoaded (matching original)

  useEffect(() => {
    if (justLoaded) {
      const t = setTimeout(() => setJustLoaded(false), 0); // allow inheritance again after this render
      return () => clearTimeout(t);
    }
  }, [justLoaded]);

  // Save scenario data (first save effect)
  useEffect(() => {
    // Skip saving if we're currently dragging
    if (
      draggingPlayerId !== null ||
      draggingUtilityId !== null ||
      draggingTextId !== null ||
      draggingImageId !== null
    ) {
      return;
    }

    if (justLoaded) {
      setJustLoaded(false);
      return;
    }

    const current = scenarioData[activeStep] || {};

    const isSame =
      JSON.stringify(current.playersOnCanvas) === JSON.stringify(playersOnCanvas) &&
      JSON.stringify(current.utilitiesOnCanvas) === JSON.stringify(utilitiesOnCanvas) &&
      JSON.stringify(current.textBoxes) === JSON.stringify(textBoxes) &&
      JSON.stringify(current.drawnLines) === JSON.stringify(drawnLines) &&
      JSON.stringify(current.imagesOnCanvas) === JSON.stringify(imagesOnCanvas) &&
      JSON.stringify(current.visionCones) === JSON.stringify(visionCones);

    if (!isSame) {
      const timeout = setTimeout(() => {
        setScenarioData(prev => ({
          ...prev,
          [activeStep]: {
            stepId: activeStep,
            playersOnCanvas,
            utilitiesOnCanvas,
            textBoxes,
            drawnLines,
            imagesOnCanvas,
            visionCones,
          }
        }));
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [
    activeStep,
    justLoaded,
    playersOnCanvas,
    utilitiesOnCanvas,
    textBoxes,
    drawnLines,
    imagesOnCanvas,
    visionCones,
    draggingPlayerId,
    draggingUtilityId,
    draggingTextId,
    draggingImageId
  ]);

  // Save scenario data on changes (second save effect)
  useEffect(() => {
    // Skip saving if we're currently dragging
    if (
      draggingPlayerId !== null ||
      draggingUtilityId !== null ||
      draggingTextId !== null ||
      draggingImageId !== null
    ) {
      return;
    }

    if (
      !canvasLoadedRef.current ||
      justLoaded ||
      loadedStepsLocked ||
      suspendHistoryRef.current ||
      isInheritingRef.current
    ) return;

    const current = scenarioData[activeStep] || {};
    const isSame =
      JSON.stringify(current.playersOnCanvas) === JSON.stringify(playersOnCanvas) &&
      JSON.stringify(current.utilitiesOnCanvas) === JSON.stringify(utilitiesOnCanvas) &&
      JSON.stringify(current.textBoxes) === JSON.stringify(textBoxes) &&
      JSON.stringify(current.drawnLines) === JSON.stringify(drawnLines) &&
      JSON.stringify(current.imagesOnCanvas) === JSON.stringify(imagesOnCanvas) &&
      JSON.stringify(current.visionCones) === JSON.stringify(visionCones);

    if (isSame) return;

    const snapshot = {
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
    };

    setScenarioData(prev => ({
      ...prev,
      [activeStep]: {
        ...(prev[activeStep] || {}),
        stepId: activeStep,
        manuallyModified: true,
        wasInherited: false,
        ...snapshot,
      }
    }));
  }, [
    playersOnCanvas,
    utilitiesOnCanvas,
    textBoxes,
    drawnLines,
    imagesOnCanvas,
    visionCones,
    activeStep,
    justLoaded,
    loadedStepsLocked,
    draggingPlayerId,
    draggingUtilityId,
    draggingTextId,
    draggingImageId,
  ]);

  // History initialization effect
  useEffect(() => {
    const h = historyRef.current[activeStep];

    const snapshot = {
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
    };

    const last = h.past[h.past.length - 1];
    const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);

    if (!isDuplicate && h.past.length === 0) {
      h.past.push(snapshot);
      h.future = [];

      if (h.past.length > 30) {
        h.past.shift();
      }
    }
  }, [activeStep]);

  // Watchdog effect for inheritance
  useEffect(() => {
    // Skip if we're currently inheriting to prevent loops
    if (skipWatchdogRef.current || isInheritingRef.current) return;
    
    if (activeStep <= 1) return; // only recheck for steps > 1

    const current = scenarioData?.[activeStep];
    const previous = scenarioData?.[activeStep - 1];
    if (!previous || !current) return;

    // Don't run if step was manually modified
    if (current.manuallyModified) return;

    // Only run watchdog if step was inherited
    if (!current.wasInherited || current.lastInheritedFrom !== activeStep - 1) return;

    const latestSnapshot = {
      playersOnCanvas: previous.playersOnCanvas || [],
      utilitiesOnCanvas: previous.utilitiesOnCanvas || [],
      drawnLines: previous.drawnLines || [],
      textBoxes: previous.textBoxes || [],
      imagesOnCanvas: previous.imagesOnCanvas || [],
      visionCones: previous.visionCones || [],
    };
    const latestHash = hash(latestSnapshot);

    // Only trigger re-inheritance if hash actually changed
    if (current.sourceHash !== latestHash) {
      console.log("✅ Triggering Re-Inheritance via setJustLoaded(false)");
      skipWatchdogRef.current = true; // Prevent watchdog from running during re-inheritance
      setJustLoaded(false);
      setTimeout(() => {
        skipWatchdogRef.current = false;
      }, 200);
    }
  }, [scenarioData, activeStep]);

  // ============================================
  // FUNCTIONS
  // ============================================

  const deleteCurrentSequenceStep = () => {
    setPlayersOnCanvas([]);
    setUtilitiesOnCanvas([]);
    setTextBoxes([]);
    setImagesOnCanvas([]);
    setDrawnLines([]);
    setVisionCones([]);

    setScenarioData(prev => ({
      ...prev,
      [activeStep]: {
        stepId: activeStep,
        playersOnCanvas: [],
        utilitiesOnCanvas: [],
        textBoxes: [],
        drawnLines: [],
        imagesOnCanvas: [],
        visionCones: [],
      }
    }));
  };

  const getLastNonEmptyStep = (scenarioData, currentStep) => {
    for (let i = currentStep - 1; i >= 1; i--) {
      const step = scenarioData[i];
      const hasData = step && Object.entries(step)
        .filter(([key]) => key !== 'stepId')
        .some(([_, val]) => Array.isArray(val) && val.length > 0);
      if (hasData) return i;
    }
    return null;
  };

  const handleStepSwitch = (newStep) => {
    if (newStep === activeStep) return; // Don't switch to the same step
    
    // Capture the current step number BEFORE any state changes
    const currentStep = activeStep;
    
    // Capture current step's data (deep copy to avoid reference issues)
    const currentStepData = {
      stepId: currentStep,
      playersOnCanvas: JSON.parse(JSON.stringify(playersOnCanvas)),
      utilitiesOnCanvas: JSON.parse(JSON.stringify(utilitiesOnCanvas)),
      textBoxes: JSON.parse(JSON.stringify(textBoxes)),
      drawnLines: JSON.parse(JSON.stringify(drawnLines)),
      imagesOnCanvas: JSON.parse(JSON.stringify(imagesOnCanvas)),
      visionCones: JSON.parse(JSON.stringify(visionCones)),
    };
    
    // Save current step's data and get updated scenarioData
    setScenarioData(prev => {
      const updated = {
        ...prev,
        [currentStep]: currentStepData
      };
      
      // Load the new step's data from the updated scenarioData
      const newStepData = updated[newStep];
      if (newStepData) {
        // Load immediately without delay
        setActiveStep(newStep);
        setPlayersOnCanvas(newStepData.playersOnCanvas || []);
        setUtilitiesOnCanvas(newStepData.utilitiesOnCanvas || []);
        setDrawnLines(newStepData.drawnLines || []);
        setTextBoxes(newStepData.textBoxes || []);
        setImagesOnCanvas(newStepData.imagesOnCanvas || []);
        setVisionCones(newStepData.visionCones || []);
      } else {
        // Initialize empty step if it doesn't exist
        setActiveStep(newStep);
        setPlayersOnCanvas([]);
        setUtilitiesOnCanvas([]);
        setDrawnLines([]);
        setTextBoxes([]);
        setImagesOnCanvas([]);
        setVisionCones([]);
      }
      
      return updated;
    });
  };

  // ============================================
  // RETURN
  // ============================================
  return {
    // State
    activeStep,
    scenarioData,
    justLoaded,
    stepSwitching,
    loadedStepsLocked,
    
    // Setters
    setActiveStep,
    setScenarioData,
    setJustLoaded,
    setStepSwitching,
    setLoadedStepsLocked,
    
    // Functions
    handleStepSwitch,
    deleteCurrentSequenceStep,
    getLastNonEmptyStep,
    
    // Refs
    historyRef,
    suspendHistoryRef,
  };
};

